import { LocalizationProvider, DateTimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useEffect, useMemo } from "react";
import { useJnx } from "../../util/jnx";
import getPathFromId from "../../util/getPathFromId";
import { getDateTimeBounds } from "../../util/dates";
import { useSideChannelSubscription } from "../../util/useSideChannel";

const getLocalTimeZone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

export default function MuiDateTimePicker({
  formData: value,
  onChange,
  uiSchema,
  readonly,
  idSchema: { $id } = {},
  formContext: { setFormDataValues, sideChannel },
}) {
  const {
    "ui:minutesStep": minutesStep,
    "ui:timeSteps": timeSteps,
    "ui:minDate": minDateOptions,
    "ui:maxDate": maxDateOptions,
    "ui:onSelect": onSelectExpr,
    "ui:minTime": minTimeOption,
    "ui:maxTime": maxTimeOption,
    "ui:readonlyExpr": readonlyExpr,
  } = uiSchema;

  const localTimeZone = useMemo(() => getLocalTimeZone(), []);
  const isAdminClockOut = useMemo(() => $id?.includes("adminClockOut"), [$id]);

  const functionBinds = useMemo(() => ({ set: setFormDataValues }), [
    setFormDataValues,
  ]);
  const onSelectJnx = useJnx(onSelectExpr, { functionBinds });
  const rootFormData = useSideChannelSubscription(sideChannel, 0);
  const path = useMemo(() => getPathFromId($id), [$id]);
  const readonlyJnx = useJnx(readonlyExpr);
  const computedReadonly = useMemo(() => {
    return readonlyJnx ? readonlyJnx.eval(rootFormData, path, { fieldPath: path}) : false;
  }, [readonlyJnx, rootFormData, path]);
  const readonlyWithJnx = computedReadonly || readonly;

  useEffect(() => {
    if (onSelectJnx && rootFormData && path && value) {
      const timeoutId = setTimeout(() => {
        onSelectJnx.eval(rootFormData, "", { fieldPath: path, object: value });
      }, 500);

      return () => clearTimeout(timeoutId);
    }
  }, [onSelectJnx, path, value]);

   const hasLimitMinDateOption = minDateOptions === 'limitDate';
   const hasLimitMaxDateOption = maxDateOptions === 'limitDate';
   const needsBounds = hasLimitMinDateOption || hasLimitMaxDateOption;
   const {
     minDate: minDateAllowed,
     maxDate: maxDateAllowed,
     minDateTime,
     maxDateTime,
     isOvernight,
   } = needsBounds
     ? getDateTimeBounds({
         startEndTime: rootFormData?.editShiftInstance?.header?.StartEndTimes,
         shiftDate: rootFormData?.editShiftInstance?.header?.DateFormatted,
       })
     : {};  
    const minDate = minDateOptions === "current" ? dayjs() : hasLimitMinDateOption ? minDateAllowed : null;
    const maxDate = maxDateOptions === "current" ? dayjs() : hasLimitMaxDateOption ? maxDateAllowed : null;
    const defaultMinTime = dayjs().set('hour', 0).startOf('hour');
    const crossMidnight = hasLimitMinDateOption && hasLimitMaxDateOption && maxDateTime?.hour() < minDateTime?.hour();
    const timePickerLimit = needsBounds
      ? {
          minTime: hasLimitMinDateOption ? minDateTime : defaultMinTime,
          maxTime: hasLimitMaxDateOption ? maxDateTime : null,
        }
      : { minTime: defaultMinTime };
    const dateTimePickerLimits =
      hasLimitMinDateOption &&
      hasLimitMaxDateOption &&
      (crossMidnight || isOvernight)
        ? { minDateTime, maxDateTime }
        : timePickerLimit;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DateTimePicker
        value={value ? dayjs(value) : null}
        onChange={(newValue) => dayjs(newValue).isValid() ? onChange(newValue.toISOString()) : null}
        minutesStep={minutesStep}
        {...dateTimePickerLimits}
        minDate={minDate}
        maxDate={maxDate}
        timeSteps={timeSteps}
        timezone={localTimeZone}
        readOnly={readonlyWithJnx}
        slotProps={{
          textField: { inputProps: { readOnly: true } },
          field: { readOnly: true },
        }}
      />
    </LocalizationProvider>
  );
}